import React from 'react';
import './LandingPage.css';

// Mid-game position rendered in the hero. Coordinates are [row][col].
// Row 0 = white's back rank (rendered at the bottom). Row 8 = black's back rank.
const HERO_POSITION = (() => {
  const board = Array(9).fill(null).map(() => Array(9).fill(null));
  const set = (r, c, type, color) => { board[r][c] = { type, color }; };

  // Black back rank (mostly intact, knight on col 7 has hopped out)
  set(8, 0, 'donkey',  'black');
  set(8, 1, 'knight',  'black');
  set(8, 2, 'camel',   'black');
  set(8, 3, 'advisor', 'black');
  set(8, 4, 'king',    'black');
  set(8, 5, 'advisor', 'black');
  set(8, 6, 'camel',   'black');
  set(8, 8, 'donkey',  'black');

  // Black middle rank
  set(7, 1, 'cannon', 'black');
  set(7, 3, 'ram',    'black');
  set(7, 4, 'spy',    'black');
  set(7, 5, 'ram',    'black');

  // Black pawns (some advanced or traded)
  set(6, 0, 'pawn', 'black');
  set(6, 2, 'pawn', 'black');
  set(6, 4, 'pawn', 'black');
  set(6, 7, 'pawn', 'black');
  set(6, 8, 'pawn', 'black');

  // Mid-board action
  set(5, 1, 'pawn',   'black'); // black pawn advanced
  set(5, 3, 'knight', 'black'); // black knight from col 1 hopped out
  set(5, 6, 'pawn',   'black'); // black pawn advanced

  set(4, 5, 'cannon', 'white'); // white cannon swung onto an open file
  set(4, 7, 'knight', 'black'); // black col-7 knight developed

  set(3, 2, 'knight', 'white'); // white knight developed
  set(3, 5, 'pawn',   'white'); // white pawn pushed up

  // White pawns (mirroring trades)
  set(2, 0, 'pawn', 'white');
  set(2, 2, 'pawn', 'white');
  set(2, 4, 'pawn', 'white');
  set(2, 6, 'pawn', 'white');
  set(2, 7, 'pawn', 'white');
  set(2, 8, 'pawn', 'white');

  // White middle rank
  set(1, 1, 'cannon', 'white');
  set(1, 3, 'ram',    'white');
  set(1, 4, 'spy',    'white');
  set(1, 5, 'ram',    'white');

  // White back rank (knight from col 1 has moved)
  set(0, 0, 'donkey',  'white');
  set(0, 2, 'camel',   'white');
  set(0, 3, 'advisor', 'white');
  set(0, 4, 'king',    'white');
  set(0, 5, 'advisor', 'white');
  set(0, 6, 'camel',   'white');
  set(0, 7, 'knight',  'white');
  set(0, 8, 'donkey',  'white');

  return board;
})();

// Squares we want to highlight as a "live tactic" — the white cannon
// at (4,5) is eyeing the black king's file with the spy and pawn behind it.
const HIGHLIGHTED_SQUARES = new Set(['4-5', '5-3', '3-2']);

const PIECES = [
  { type: 'king',    name: 'King',    line: 'Moves 1 square in any direction. Lose it and the game is over.' },
  { type: 'advisor', name: 'Advisor', line: 'Moves 1 or 2 squares in any direction. Your king\'s right hand.' },
  { type: 'cannon',  name: 'Cannon',  line: 'Slides like a rook — and jumps a single piece to capture.' },
  { type: 'spy',     name: 'Spy',     line: 'Moves 1 square any direction, or unlimited squares backward. Reach the home row and freeze the king.' },
  { type: 'ram',     name: 'Ram',     line: 'Slides like a rook and bumps pieces backward. Bumped off the board = captured.' },
  { type: 'knight',  name: 'Knight',  line: 'L-shaped (2+1). The classic chess knight, jumps over pieces.' },
  { type: 'camel',   name: 'Camel',   line: 'Extended knight (3+1). Reaches further than any knight expects.' },
  { type: 'donkey',  name: 'Donkey',  line: '1 square any direction, or 2 diagonally on the same color. Surprising mobility.' },
  { type: 'pawn',    name: 'Pawn',    line: 'Forward, captures diagonally, promotes on the last row.' },
];

const PieceImg = ({ piece }) => (
  <img
    className="piece-img"
    src={`/assets/pieces/${piece.color}-${piece.type}.png`}
    alt={`${piece.color} ${piece.type}`}
    draggable="false"
  />
);

const HeroBoard = () => {
  // Render with row 8 at the top so the visual matches a normal board.
  const rows = [];
  for (let r = 8; r >= 0; r--) {
    const cells = [];
    for (let c = 0; c < 9; c++) {
      const piece = HERO_POSITION[r][c];
      const isLight = (r + c) % 2 === 0;
      const isHighlight = HIGHLIGHTED_SQUARES.has(`${r}-${c}`);
      cells.push(
        <div
          key={c}
          className={`hero-square ${isLight ? 'light' : 'dark'} ${isHighlight ? 'highlight' : ''}`}
        >
          {piece && <PieceImg piece={piece} />}
        </div>
      );
    }
    rows.push(
      <div key={r} className="hero-row">
        {cells}
      </div>
    );
  }
  return <div className="hero-board">{rows}</div>;
};

const LandingPage = ({ onLogin, onSignup, onOrder }) => {
  return (
    <div className="landing-page">
      {/* Hero */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="nav-logo">
            <h1>♜ Ranga</h1>
          </div>
          <div className="nav-buttons">
            {onOrder && (
              <button onClick={onOrder} className="btn-secondary">Order</button>
            )}
            <button onClick={onLogin} className="btn-secondary">Log In</button>
            <button onClick={onSignup} className="btn-primary">Sign Up</button>
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">A new battlefield.</h1>
            <p className="hero-subtitle">
              Ranga (Sanskrit for "battlefield") combines elements of Xianqi, Shogi and
              Western Chess to create a new and exciting strategy game.
            </p>
            <div className="hero-buttons">
              <button onClick={onSignup} className="btn-hero-primary">
                Play Now — It's Free
              </button>
              <button onClick={onLogin} className="btn-hero-secondary">
                Sign In
              </button>
            </div>
            <p className="hero-note">No download. Play in your browser.</p>
          </div>

          <div className="hero-image">
            <div className="board-frame">
              <HeroBoard />
              <div className="board-caption">
                White cannon eyes a jump-capture down the open file.
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Meet the Pieces */}
      <section className="pieces-section">
        <div className="section-head">
          <h2>Meet the nine pieces</h2>
          <p>Five you've seen before. Four you haven't.</p>
        </div>
        <div className="pieces-grid">
          {PIECES.map((p) => (
            <div className="piece-card" key={p.type}>
              <div className="piece-card-art">
                <img
                  src={`/assets/pieces/white-${p.type}.png`}
                  alt={p.name}
                  draggable="false"
                />
              </div>
              <h3>{p.name}</h3>
              <p>{p.line}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI Feature */}
      <section className="feature-section alternate">
        <div className="feature-content">
          <div className="feature-text">
            <h2>Play a real engine, not a toy</h2>
            <p>
              Our C++ engine runs alpha-beta search with a neural network evaluator
              trained on self-play. Pick a difficulty and it'll punish a hanging piece
              the same way a strong human would.
            </p>
            <ul className="feature-list">
              <li>Adjustable depth — friendly to grandmaster-strength</li>
              <li>NNUE evaluation with self-play training</li>
              <li>Move analysis, opening book, and tactical puzzles</li>
              <li>Configurable time controls</li>
            </ul>
          </div>
          <div className="feature-image">
            <div className="feature-card ai-card">
              <div className="card-icon">⚙️</div>
              <h3>The Engine</h3>
              <p>Native C++ • neural eval • self-play tuned</p>
            </div>
          </div>
        </div>
      </section>

      {/* Multiplayer Feature */}
      <section className="feature-section">
        <div className="feature-content reverse">
          <div className="feature-image">
            <div className="feature-card multiplayer-card">
              <div className="card-icon">⚔️</div>
              <h3>Real-time matches</h3>
              <p>Live boards over WebSocket</p>
            </div>
          </div>
          <div className="feature-text">
            <h2>Bring a friend, share a link</h2>
            <p>
              Send a game link, both of you load the board, and the moves stream live.
              Casual, ranked, or just a quick friendly — pick your time control and play.
            </p>
            <ul className="feature-list">
              <li>Real-time multiplayer over WebSocket</li>
              <li>Casual and ranked queues</li>
              <li>Game history and replays</li>
              <li>Track your rating over time</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Learn Feature */}
      <section className="feature-section alternate">
        <div className="feature-content">
          <div className="feature-text">
            <h2>Learn the new pieces fast</h2>
            <p>
              You already know how a knight moves. We'll get you up to speed on
              the rest — Cannon, Camel, Donkey, Ram, Spy — with interactive
              tutorials and tactical puzzles built around each one.
            </p>
            <ul className="feature-list">
              <li>Step-by-step tutorial for every piece</li>
              <li>Daily tactical puzzles</li>
              <li>Game analysis on every match</li>
              <li>Opening book to study lines</li>
            </ul>
          </div>
          <div className="feature-image">
            <div className="feature-card learn-card">
              <div className="card-icon">🧩</div>
              <h3>Tutorials & Puzzles</h3>
              <p>From your first cannon jump to spy-king endgames</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <h2>Ready for the first cannon jump of your life?</h2>
        <p>Free account. No download. Plays in any browser.</p>
        <button onClick={onSignup} className="btn-cta">
          Create Free Account
        </button>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Ranga</h4>
            <p>A new battlefield. Xianqi, Shogi and Western Chess combined into a new strategy game.</p>
          </div>
          <div className="footer-section">
            <h4>Play</h4>
            <ul>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Online Game</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Vs Computer</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Puzzles</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Learn</h4>
            <ul>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>How to Play</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Tutorials</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>Strategies</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Ranga. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
